/**
 * UpdateBanner
 * 
 * Banner fixo que aparece quando há uma nova versão disponível.
 * Fica visível até o usuário clicar para atualizar ou dispensar.
 */
import { useEffect, useState } from 'react';
import { RefreshCw, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useVersionCheck } from '@/hooks/useVersionCheck';

const DISMISS_KEY = 'update_banner_dismissed_until';
const DISMISS_MS = 24 * 60 * 60 * 1000; // 24h

export function UpdateBanner() {
  const {
    updateAvailable,
    serverVersion,
    isUpdating,
    updateStep,
    performUpdate,
  } = useVersionCheck();

  const [dismissed, setDismissed] = useState(false);

  // Persistir dispensa por 24h para não voltar no login a cada reload
  useEffect(() => {
    try {
      const untilRaw = localStorage.getItem(DISMISS_KEY);
      if (!untilRaw) return;
      const until = Number(untilRaw);
      if (!Number.isFinite(until)) return;
      if (Date.now() < until) setDismissed(true);
    } catch {
      // ignore
    }
  }, []);

  if (!updateAvailable || dismissed) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-[100] max-w-md mx-auto animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-primary text-primary-foreground rounded-lg shadow-lg p-4 flex items-center gap-3">
        {isUpdating ? (
          <Loader2 className="w-5 h-5 flex-shrink-0 animate-spin" />
        ) : (
          <RefreshCw className="w-5 h-5 flex-shrink-0 animate-pulse" />
        )}
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            {isUpdating ? updateStep || 'Atualizando...' : 'Nova versão disponível'}
          </p>
          <p className="text-xs opacity-80 truncate">
            {isUpdating
              ? 'Aguarde, não feche o app'
              : `Versão ${serverVersion?.version || 'nova'} pronta`}
          </p>
        </div>
        
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            size="sm"
            variant="secondary"
            className="h-8 px-3 text-xs"
            onClick={performUpdate}
            disabled={isUpdating}
          >
            {isUpdating ? 'Aguarde...' : 'Atualizar'}
          </Button>
          
          {!isUpdating && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
              onClick={() => {
                setDismissed(true);
                try {
                  localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
                } catch {
                  // ignore
                }
              }}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
